import {
  BRAND,
  BRAND_CTA_GRADIENT,
  BRAND_CTA_GRADIENT_HOVER,
  BRAND_GRADIENT,
  FONT_STACK,
  NAV_HEIGHT,
  RADIUS,
  SHADOW,
  SPACE,
  TRANSITION,
} from "./brand.js"
import {
  INK,
  SERIES_DARK,
  SERIES_LIGHT,
  STATUS,
  STATUS_CHIP,
  STATUS_INK,
  SURFACE,
  SURFACE_SUBTLE,
  type ThemeMode,
} from "./palette.js"

export type AntdThemeTokens = {
  token: Record<string, string | number>
  components: Record<string, Record<string, string | number>>
}

export function antdTheme(mode: ThemeMode): AntdThemeTokens {
  const series = mode === "dark" ? SERIES_DARK : SERIES_LIGHT
  const c = INK[mode]
  const bg = SURFACE[mode]
  const subtle = SURFACE_SUBTLE[mode]
  const chip = STATUS_CHIP[mode]

  return {
    token: {
      colorPrimary: series[0],
      colorInfo: series[0],
      colorSuccess: STATUS.good,
      colorWarning: STATUS.warning,
      colorError: STATUS.critical,

      colorSuccessBg: chip.good.surface,
      colorSuccessBorder: chip.good.border,
      colorSuccessText: chip.good.text,
      colorWarningBg: chip.warning.surface,
      colorWarningBorder: chip.warning.border,
      colorWarningText: chip.warning.text,
      colorErrorBg: chip.critical.surface,
      colorErrorBorder: chip.critical.border,
      colorErrorText: chip.critical.text,
      colorInfoBg: chip.info.surface,
      colorInfoBorder: chip.info.border,
      colorInfoText: chip.info.text,

      colorBgContainer: bg,
      colorBgLayout: bg,
      colorBgElevated: bg,
      colorFillAlter: subtle,
      colorFillQuaternary: subtle,

      colorText: c.primary,
      colorTextSecondary: c.secondary,
      colorTextTertiary: c.muted,
      colorTextQuaternary: c.muted,
      colorTextPlaceholder: c.muted,
      colorBorder: c.gridline,
      colorBorderSecondary: c.gridline,
      colorSplit: c.gridline,

      colorLink: series[0],
      colorLinkHover: mode === "dark" ? series[0] : BRAND.navyHover,
      colorLinkActive: mode === "dark" ? series[0] : BRAND.navyHover,

      borderRadius: RADIUS.button,
      borderRadiusLG: RADIUS.card,
      borderRadiusSM: RADIUS.badge,
      borderRadiusXS: 4,

      boxShadow: SHADOW.card,
      boxShadowSecondary: SHADOW.dropdown,
      boxShadowTertiary: SHADOW.dropdown,
      controlOutline: "rgba(0,85,148,0.15)",
      controlOutlineWidth: 2,

      fontSize: 14,
      fontFamily: FONT_STACK,
    },
    components: {
      Table: { headerBg: "transparent", borderColor: c.gridline, rowHoverBg: subtle, margin: 4 },
      Menu: { itemHeight: 36, itemBorderRadius: RADIUS.badge, itemSelectedBg: subtle, itemSelectedColor: series[0] },
      Layout: { siderBg: bg, headerBg: bg, bodyBg: bg },
      Card: { colorBorderSecondary: c.gridline, borderRadiusLG: RADIUS.card },
      // The brand's pill radius. Scoped to Tag so it cannot reach small buttons, which stay at the 8px button radius
      Tag: { borderRadiusSM: RADIUS.pill, defaultBg: subtle, defaultColor: c.secondary },
      Input: { colorBgContainer: subtle, activeBorderColor: series[0], hoverBorderColor: series[0] },
      InputNumber: { colorBgContainer: subtle, activeBorderColor: series[0], hoverBorderColor: series[0] },
      Select: { colorBgContainer: subtle, optionSelectedBg: subtle, borderRadius: RADIUS.input },
      DatePicker: { colorBgContainer: subtle, activeBorderColor: series[0] },
      Dropdown: { borderRadiusLG: RADIUS.dropdown },
      Popover: { borderRadiusLG: RADIUS.dropdown },
      Modal: { borderRadiusLG: RADIUS.card, boxShadow: SHADOW.modal },
      Segmented: { trackBg: subtle, itemSelectedBg: bg },
      Tooltip: { borderRadius: RADIUS.dropdown },
    },
  }
}

export function brandVariables(): Record<string, string> {
  return {
    "--rd-navy": BRAND.navy,
    "--rd-navy-hover": BRAND.navyHover,
    "--rd-sky": BRAND.sky,
    "--rd-ice": BRAND.ice,
    "--rd-amber": BRAND.amber,
    "--rd-orange": BRAND.orange,
    "--rd-red": BRAND.red,
    "--rd-gradient": BRAND_GRADIENT,
    "--rd-cta-gradient": BRAND_CTA_GRADIENT,
    "--rd-cta-gradient-hover": BRAND_CTA_GRADIENT_HOVER,

    "--rd-font": FONT_STACK,
    "--rd-transition": TRANSITION,
    "--rd-nav-height": `${NAV_HEIGHT}px`,

    "--rd-radius-button": `${RADIUS.button}px`,
    "--rd-radius-card": `${RADIUS.card}px`,
    "--rd-radius-dropdown": `${RADIUS.dropdown}px`,
    "--rd-radius-badge": `${RADIUS.badge}px`,
    "--rd-radius-pill": `${RADIUS.pill}px`,

    "--rd-shadow-card": SHADOW.card,
    "--rd-shadow-modal": SHADOW.modal,
    "--rd-shadow-dropdown": SHADOW.dropdown,
    "--rd-shadow-focus": SHADOW.focus,

    ...Object.fromEntries(Object.entries(SPACE).map(([k, v]) => [`--rd-space-${k}`, `${v}px`])),
  }
}

export function cssVariables(mode: ThemeMode): Record<string, string> {
  const series = mode === "dark" ? SERIES_DARK : SERIES_LIGHT
  const c = INK[mode]
  const statusInk = STATUS_INK[mode]
  const chip = STATUS_CHIP[mode]

  return {
    "--csbi-surface": SURFACE[mode],
    "--csbi-surface-subtle": SURFACE_SUBTLE[mode],
    "--csbi-ink-primary": c.primary,
    "--csbi-ink-secondary": c.secondary,
    "--csbi-ink-muted": c.muted,
    "--csbi-gridline": c.gridline,
    "--csbi-baseline": c.baseline,
    "--csbi-delta-good": c.deltaGood,

    "--csbi-status-good": STATUS.good,
    "--csbi-status-warning": STATUS.warning,
    "--csbi-status-serious": STATUS.serious,
    "--csbi-status-critical": STATUS.critical,
    ...Object.fromEntries(Object.entries(statusInk).map(([k, v]) => [`--csbi-status-${k}-ink`, v])),
    ...Object.fromEntries(
      Object.entries(chip).flatMap(([name, v]) => [
        [`--csbi-chip-${name}-bg`, v.surface],
        [`--csbi-chip-${name}-border`, v.border],
        [`--csbi-chip-${name}-text`, v.text],
      ]),
    ),

    ...Object.fromEntries(series.map((hex, i) => [`--csbi-series-${i + 1}`, hex])),
  }
}

export function themeStylesheet(): string {
  const declarations = (vars: Record<string, string>) =>
    Object.entries(vars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join("\n")

  return [
    "/* Generated by themeStylesheet() in @csbi/viz-core — do not edit by hand. */",
    `:root {\n${declarations({ ...brandVariables(), ...cssVariables("light") })}\n}`,
    `[data-theme="dark"] {\n${declarations(cssVariables("dark"))}\n}`,
  ].join("\n\n")
}
